import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="tabs"
export default class extends Controller {
  static targets = ["tab", "panel", "select", "tabList", "progressBar", "template"]
  
  static values = {
    index: { type: Number, default: 0 },
    updateAnchor: { type: Boolean, default: false },
    scrollToAnchor: { type: Boolean, default: false },
    scrollActiveTabIntoView: { type: Boolean, default: false },
    autoSwitch: { type: Boolean, default: false },
    autoSwitchInterval: { type: Number, default: 5000 },
    pauseOnHover: { type: Boolean, default: true },
    showProgressBar: { type: Boolean, default: false },
    lazyLoad: { type: Boolean, default: false },
    turboFrameSrc: { type: String, default: "" }
  }

  static classes = ["activeTab", "inactiveTab"]

  connect() {
    // Set initial active tab
    this.showTab(this.indexValue)
    
    // Handle URL anchors if enabled
    if (this.updateAnchorValue && window.location.hash) {
      this.activateTabFromHash()
    }
    
    // Listen for hash changes
    if (this.updateAnchorValue) {
      window.addEventListener("hashchange", this.handleHashChange)
    }
    
    // Setup auto-switching if enabled
    if (this.autoSwitchValue) {
      this.setupAutoSwitch()
    }
  }

  disconnect() {
    if (this.updateAnchorValue) {
      window.removeEventListener("hashchange", this.handleHashChange)
    }
    
    if (this.autoSwitchTimer) {
      clearInterval(this.autoSwitchTimer)
    }
    
    if (this.progressBarTimer) {
      clearInterval(this.progressBarTimer)
    }
  }

  // Change tab on click or select
  change(event) {
    event.preventDefault()
    
    let index
    
    // Check if this is from a select element
    if (event.target.tagName === "SELECT") {
      index = parseInt(event.target.value)
    } else {
      index = this.tabIndexFor(event.currentTarget)
    }
    
    if (index >= 0 && index < this.tabTargets.length) {
      this.indexValue = index
      this.showTab(index)
      
      // Reset auto-switch timer if enabled
      if (this.autoSwitchValue) {
        this.resetAutoSwitch()
      }
    }
  }

  // Which tab an event came from. The action is usually declared on the
  // <button> INSIDE the tab target rather than on the target itself, so a
  // bare indexOf(currentTarget) returns -1 and the click is silently
  // discarded. Accept either element.
  tabIndexFor(element) {
    return this.tabTargets.findIndex((tab) => tab === element || tab.contains(element))
  }

  // Keyboard navigation
  keydown(event) {
    const currentIndex = this.tabIndexFor(event.currentTarget)
    if (currentIndex < 0) return

    let targetIndex = currentIndex
    
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault()
        targetIndex = currentIndex + 1
        if (targetIndex >= this.tabTargets.length) {
          targetIndex = 0
        }
        break
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault()
        targetIndex = currentIndex - 1
        if (targetIndex < 0) {
          targetIndex = this.tabTargets.length - 1
        }
        break
      case "Home":
        event.preventDefault()
        targetIndex = 0
        break
      case "End":
        event.preventDefault()
        targetIndex = this.tabTargets.length - 1
        break
      default:
        return
    }
    
    this.indexValue = targetIndex
    this.showTab(targetIndex)
    
    // Focus the new tab
    this.tabTargets[targetIndex].querySelector("button, a")?.focus()
    
    // Reset auto-switch timer if enabled
    if (this.autoSwitchValue) {
      this.resetAutoSwitch()
    }
  }

  // Show specific tab by index
  showTab(index) {
    // Hide all panels and deactivate all tabs
    this.panelTargets.forEach((panel, i) => {
      if (i === index) {
        panel.hidden = false
        panel.setAttribute("aria-hidden", "false")
        
        // Load content if lazy loading is enabled
        if (this.lazyLoadValue) {
          this.loadTabContent(panel, i)
        }
      } else {
        panel.hidden = true
        panel.setAttribute("aria-hidden", "true")
      }
    })
    
    // Update tab states
    this.tabTargets.forEach((tab, i) => {
      const button = tab.querySelector("button, a")
      
      if (i === index) {
        // Activate tab
        tab.setAttribute("data-active", "true")
        button?.setAttribute("aria-selected", "true")
        button?.setAttribute("tabindex", "0")
        
        // Apply active classes
        if (this.hasActiveTabClass) {
          this.activeTabClasses.forEach(cls => tab.classList.add(cls))
        }
        if (this.hasInactiveTabClass) {
          this.inactiveTabClasses.forEach(cls => tab.classList.remove(cls))
        }
        
        // Scroll into view if enabled
        if (this.scrollActiveTabIntoViewValue) {
          tab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" })
        }
      } else {
        // Deactivate tab
        tab.setAttribute("data-active", "false")
        button?.setAttribute("aria-selected", "false")
        button?.setAttribute("tabindex", "-1")
        
        // Apply inactive classes
        if (this.hasActiveTabClass) {
          this.activeTabClasses.forEach(cls => tab.classList.remove(cls))
        }
        if (this.hasInactiveTabClass) {
          this.inactiveTabClasses.forEach(cls => tab.classList.add(cls))
        }
      }
    })
    
    // Update select if present
    if (this.hasSelectTarget) {
      this.selectTarget.value = index
    }
    
    // Update URL anchor if enabled
    if (this.updateAnchorValue) {
      const panel = this.panelTargets[index]
      const anchorId = panel.id
      if (anchorId) {
        history.pushState(null, "", `#${anchorId}`)
        
        if (this.scrollToAnchorValue) {
          panel.scrollIntoView({ behavior: "smooth" })
        }
      }
    }
    
    // Dispatch custom event
    this.dispatch("change", {
      detail: {
        activeIndex: index,
        activeTab: this.tabTargets[index],
        activePanel: this.panelTargets[index]
      }
    })
  }

  // Load tab content via Turbo Frame or template
  loadTabContent(panel, index) {
    // Check for Turbo Frame lazy loading
    const turboFrame = panel.querySelector("turbo-frame[data-tabs-frame]")
    if (turboFrame && !turboFrame.hasAttribute("src")) {
      // Get the URL from the data attribute
      const url = turboFrame.dataset.tabUrl
      if (url) {
        turboFrame.setAttribute("src", url)
      }
    }
    
    // Check for template-based lazy loading
    if (this.hasTemplateTarget && this.templateTargets[index]) {
      const template = this.templateTargets[index]
      const content = template.content.cloneNode(true)
      
      // Clear loading state and insert content
      panel.innerHTML = ""
      panel.appendChild(content)
    }
  }

  // Activate tab from URL hash
  activateTabFromHash() {
    const hash = window.location.hash.slice(1)
    if (!hash) return
    
    const panelIndex = this.panelTargets.findIndex(panel => panel.id === hash)
    if (panelIndex >= 0) {
      this.indexValue = panelIndex
      this.showTab(panelIndex)
    }
  }

  // Handle hash change
  handleHashChange = () => {
    this.activateTabFromHash()
  }

  // Setup auto-switching
  setupAutoSwitch() {
    if (this.pauseOnHoverValue) {
      this.element.addEventListener("mouseenter", this.pauseAutoSwitch)
      this.element.addEventListener("mouseleave", this.resumeAutoSwitch)
      this.element.addEventListener("focusin", this.pauseAutoSwitch)
      this.element.addEventListener("focusout", this.resumeAutoSwitch)
    }
    
    this.startAutoSwitch()
  }

  startAutoSwitch() {
    this.autoSwitchTimer = setInterval(() => {
      const nextIndex = (this.indexValue + 1) % this.tabTargets.length
      this.indexValue = nextIndex
      this.showTab(nextIndex)
    }, this.autoSwitchIntervalValue)
    
    // Setup progress bar if enabled
    if (this.showProgressBarValue && this.hasProgressBarTarget) {
      this.startProgressBar()
    }
  }

  pauseAutoSwitch = () => {
    if (this.autoSwitchTimer) {
      clearInterval(this.autoSwitchTimer)
      this.autoSwitchTimer = null
    }
    
    if (this.progressBarTimer) {
      clearInterval(this.progressBarTimer)
      this.progressBarTimer = null
    }
  }

  resumeAutoSwitch = () => {
    if (!this.autoSwitchTimer) {
      this.startAutoSwitch()
    }
  }

  resetAutoSwitch() {
    this.pauseAutoSwitch()
    this.resumeAutoSwitch()
  }

  startProgressBar() {
    if (!this.hasProgressBarTarget) return
    
    const progressBar = this.progressBarTarget
    const interval = 50 // Update every 50ms
    const totalSteps = this.autoSwitchIntervalValue / interval
    let currentStep = 0
    
    progressBar.style.width = "0%"
    
    this.progressBarTimer = setInterval(() => {
      currentStep++
      const percentage = (currentStep / totalSteps) * 100
      progressBar.style.width = `${percentage}%`
      
      if (currentStep >= totalSteps) {
        currentStep = 0
      }
    }, interval)
  }
}

